import { Trophy, RefreshCw, CheckCircle } from 'lucide-react';
import type { BrandPerformance } from '../types/statistics';
import { formatEuro } from '../utils/currency';

interface BrandRankingsProps {
  brands: BrandPerformance[];
  loading?: boolean;
}

export function BrandRankings({ brands, loading = false }: BrandRankingsProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="animate-pulse rounded-2xl bg-gray-100 p-6 h-32"></div>
        ))}
      </div>
    );
  }

  if (!brands || brands.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Nenhuma marca encontrada</p>
      </div>
    );
  }

  const getRoiConfig = (assessment: string) => {
    switch (assessment) {
      case 'Excellent':
        return { text: 'Excelente', color: 'bg-gradient-to-r from-green-400 to-emerald-600' };
      case 'Good':
        return { text: 'Bom', color: 'bg-gradient-to-r from-blue-400 to-cyan-600' };
      case 'Average':
        return { text: 'Médio', color: 'bg-gradient-to-r from-yellow-400 to-orange-500' };
      case 'Poor':
        return { text: 'Fraco', color: 'bg-gradient-to-r from-red-400 to-rose-600' };
      default:
        return { text: 'N/A', color: 'bg-gray-400' };
    }
  };

  const topThree = brands.slice(0, 3);
  const rest = brands.slice(3);

  return (
    <div className="space-y-4">
      {topThree.map((brand) => {
        const roiConfig = getRoiConfig(brand.roi_assessment);

        return (
          <div
            key={`${brand.brand}-${brand.rank}`}
            className="relative bg-gradient-to-br from-emerald-50 to-green-100/50 border-2 border-emerald-400 rounded-2xl p-6 hover:shadow-xl transition-all duration-300"
          >
            <div className="absolute -left-4 top-1/2 -translate-y-1/2 bg-emerald-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl shadow-lg">
              {brand.rank}
            </div>

            <div className="flex items-start justify-between ml-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Trophy className="text-yellow-500" size={24} />
                  <h3 className="text-xl font-bold text-gray-900">{brand.brand}</h3>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-bold text-white ${roiConfig.color}`}>
                    <CheckCircle size={14} />
                    ROI: {roiConfig.text}
                  </span>
                  {brand.is_repeat_client && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-purple-500 to-pink-500">
                      <RefreshCw size={14} />
                      Recorrente
                    </span>
                  )}
                  <span className="text-gray-600 text-sm">{brand.campaign_count} campanhas</span>
                </div>

                <div className="bg-white/60 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600">Taxa de Sucesso</p>
                    <p className="text-lg font-bold text-gray-900">{brand.success_rate.toFixed(0)}%</p>
                  </div>
                  <div className="w-full bg-emerald-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-emerald-500 to-green-600 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${brand.success_rate}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="text-right">
                <p className="text-4xl font-bold text-emerald-600">{formatEuro(brand.total_investment)}</p>
                <p className="text-sm text-gray-600 mt-1">Investimento Total</p>
                {brand.contribution_percentage !== undefined && (
                  <p className="text-sm text-gray-600 mt-1">{brand.contribution_percentage.toFixed(1)}% do total</p>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {rest.map((brand) => {
        const roiConfig = getRoiConfig(brand.roi_assessment);

        return (
          <div
            key={`${brand.brand}-${brand.rank}`}
            className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                <div className="bg-gray-200 text-gray-700 rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                  {brand.rank}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{brand.brand}</h3>
                    {brand.is_repeat_client && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-purple-500 to-pink-500">
                        <RefreshCw size={12} />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    {brand.campaign_count} campanhas • Sucesso: {brand.success_rate.toFixed(0)}% • ROI:{' '}
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold text-white ${roiConfig.color}`}>
                      {roiConfig.text}
                    </span>
                  </p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{formatEuro(brand.total_investment)}</p>
                {brand.contribution_percentage !== undefined && (
                  <p className="text-sm text-gray-600">{brand.contribution_percentage.toFixed(1)}% do total</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
